import {
  TransferScheduleWeekDay,
  TransferSchedule,
  TransferScheduleMonthBoundary,
  TransferScheduleType,
  TransferScheduleWeeklyDetails,
  TransferScheduleMonthlyByDateDetails,
} from "@/shared/types";
import moment from "moment";

export function getScheduleDescription (schedule: TransferSchedule): string {
  if (!schedule) {
    return "";
  }
  if (schedule.scheduleType === TransferScheduleType.MonthlyByDate) {
    return schedule.monthlyByDateDetails.length === 0
        ? ""
        : buildMonthlyByDateDescription(schedule);
  } else if (schedule.scheduleType === TransferScheduleType.MonthlyByDay) {
    return schedule.monthlyByDayDetails.length === 0
        ? ""
        : buildMonthlyByDayDescription(schedule);
  } else if (schedule.scheduleType === TransferScheduleType.Weekly) {
    return schedule.weeklyDetails.length === 0
        ? ""
        : buildWeeklyDescription(schedule);
  } else {
    return schedule.oneTimeDetails.length === 0
        ? ""
        : buildOneTimeDescription(schedule);
  }
}

function buildOneTimeDescription (schedule: TransferSchedule): string {
  return moment(schedule.scheduleStartDate).format("MMM D, YYYY");
}

function buildMonthlyByDateDescription (schedule: TransferSchedule): string {
  return "The " + buildDatesDescription(schedule.monthlyByDateDetails)
      + buildFrequencyTypeDescription(schedule.scheduleInterval, "month");
}

function buildMonthlyByDayDescription (schedule: TransferSchedule): string {
  const singleDetail = schedule.monthlyByDayDetails[0];

  return "The " + buildMonthDayDescription(singleDetail.dayOfWeek, singleDetail.monthBoundary)
  + buildFrequencyTypeDescription(schedule.scheduleInterval, "month");
}

function buildWeeklyDescription (schedule: TransferSchedule): string {
  return buildWeekDaysDescription(schedule.weeklyDetails)
  + buildFrequencyTypeDescription(schedule.scheduleInterval, "week");
}

function buildFrequencyTypeDescription (frequency: number, name: string): string {
  if (frequency === 1) {
    return `, every ${name}`;
  }
  else return `, every ${frequency} ${name}s`;
}

function buildMonthDayDescription (dayOfWeek: TransferScheduleWeekDay,
    frequency: TransferScheduleMonthBoundary): string {
  return `${getBoundary(frequency)} ${getWeekDay(dayOfWeek)}`;
}

function buildWeekDaysDescription (details: TransferScheduleWeeklyDetails[]): string {
  const days = details.map(x => x.dayOfWeek);
  let resultString = "";
  days.sort(dayCompare);
  days.forEach(day => {
    if (resultString === "") {
      resultString = getWeekDay(day);
    }
    else if (days.indexOf(day) !== days.length - 1) {
      resultString += `, ${getWeekDay(day)}`;
    }
    else {
      resultString += ` & ${getWeekDay(day)}`;
    }
  });
  return resultString;
}

function buildDatesDescription (dates: TransferScheduleMonthlyByDateDetails[]): string {
  let resultString = "";
  // copy, because sorting mutates the array
  const datesCopy = [...dates];
  datesCopy.sort(dateCompare);
  datesCopy.forEach(date => {
    if (resultString === "") {
      resultString = getFormattedDate(date);
    }
    else if (datesCopy.indexOf(date) !== datesCopy.length - 1) {
      resultString += `, ${getFormattedDate(date)}`;
    }
    else {
      resultString += ` & ${getFormattedDate(date)}`;
    }
  });
  return resultString;
}

function dateCompare
    (a: TransferScheduleMonthlyByDateDetails, b: TransferScheduleMonthlyByDateDetails): number {
  if (a.isLastDay) {
   return 1;
  }

  if (b.isLastDay) {
    return -1;
  }

  if (a.dayOfMonth < b.dayOfMonth) {
    return -1;
  }
  else {
    return 1;
  }
}

function dayCompare
    (a: TransferScheduleWeekDay, b: TransferScheduleWeekDay): number {
  if (weekDayIndex(a) < weekDayIndex(b)) {
    return -1;
  }

  return 1;
}

function getFormattedDate (date: TransferScheduleMonthlyByDateDetails) {
  if (date.isLastDay) {
    return "last day";
  }
  else {
    return moment("01-" + date.dayOfMonth, "MM-DD").format("Do");
  }
}

function weekDayIndex (day: TransferScheduleWeekDay): number {
  return Object.values(TransferScheduleWeekDay).indexOf(day);
}

function getWeekDay (day: TransferScheduleWeekDay): string {
  const index = weekDayIndex(day);
  return Object.keys(TransferScheduleWeekDay)[index];
}

function getBoundary (boundary: TransferScheduleMonthBoundary): string {
  return Object.keys(TransferScheduleMonthBoundary)[
    Object.values(TransferScheduleMonthBoundary).indexOf(boundary)].toLowerCase();
}